/**
 * Customer onboarding-session middleware tags (client-safe).
 *
 * The public `onboarding.*` RPCs in `public/installations.rpc.ts` are the
 * customer-facing install wizard surface. They were historically
 * unauthenticated and looked installations up by a client-supplied
 * `onboardingId` with no ownership check — an IDOR (CLA-455). This middleware
 * gates them behind the hashed magic-link token minted by the intake flow
 * (CLA-428): the caller presents the plaintext token as `Authorization:
 * Bearer <token>`, the server hashes it, resolves the intake by
 * `intakes.link_token_hash`, validates expiry, and binds the resolved
 * customer/intake onto the request context as {@link OnboardingSession}.
 *
 * Only the tag declarations live here so client packages can import them
 * without pulling in any server-only (Database) dependency — the resolution
 * logic lives in `onboarding-session.middleware.ts`.
 *
 * @since 1.0.0
 */
import { RpcMiddleware } from "@effect/rpc";
import { Context } from "effect";

import { Unauthorized } from "./errors.ts";

/**
 * The authorized onboarding session installed by
 * {@link OnboardingSessionMiddleware} after a successful magic-link token
 * verification. Scopes every downstream data access to the resolved customer:
 * handlers MUST verify any client-supplied `onboardingId` belongs to this
 * `customerId` rather than trusting the payload.
 *
 * @since 1.0.0
 */
export class OnboardingSession extends Context.Tag("OnboardingSession")<
  OnboardingSession,
  {
    readonly customerId: string;
    readonly intakeId: string;
  }
>() {}

/**
 * RPC middleware guarding the customer-facing onboarding endpoints. Resolves
 * the hashed magic-link token into an {@link OnboardingSession}; fails
 * {@link Unauthorized} when the token is missing, unknown, or expired.
 *
 * Token resolution reuses the CLA-428 hashed-token path (`intakes`
 * `link_token_hash`) — no new long-lived bearer keyed on the raw installation
 * ULID is introduced.
 *
 * @since 1.0.0
 */
export class OnboardingSessionMiddleware extends RpcMiddleware.Tag<OnboardingSessionMiddleware>()(
  "OnboardingSessionMiddleware",
  {
    provides: OnboardingSession,
    failure: Unauthorized,
  },
) {}
